import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@wds/box';
import { Grid } from '@wds/grid';
import { Button } from '@wds/button';
import { Typography } from '@wds/typography';
import { IconArrowLeft, IconArrowRight } from '@wds/icons';
import AppDetailsFooter from './components/AppDetailsFooter';
import AppDetailsInfo from './components/AppDetailsInfo';
import AppDetailsFeatures from './components/AppDetailsFeatures';
import AppDetailsJumboHeader from './components/AppDetailsJumboHeader';
import DisplayMedia from './components/DisplayMedia';
import useStyles from './styles';
import { buildAppDetailsMediaFilesList } from './AppDetails';
import AppDetailsActionButton from './components/AppDetailsActionButton';
import { useCarousel } from './components/CarouselContext';

export const getCurrentDomain = () => {
  return `${window.location.protocol}//${window.location.hostname}`;
};

export const sendMessage = (msg, domain) => {
  window.parent.postMessage(msg, domain);
};

export const AppDetailsModal = ({ appDetails, refreshDetails }) => {
  const {
    appDetailsModal,
    appDetailsModalBody,
    appDetailsHeader,
    contentContainer,
    appDetailsModalMediaOpened,
    appDetailsModalMediaClosed,
    appDetailsModalMediaWrapper,
  } = useStyles();

  const { mediaList, selectedMedia, selectMedia, updateMediaList, selectNextMedia, selectPreviousMedia } =
    useCarousel();

  const [showMedia, setShowMedia] = useState(false);

  const selectImageToShow = mediaIndex => {
    selectMedia(mediaIndex);
    setShowMedia(true);
  };

  const closeModal = () => {
    sendMessage({ message: 'sm:close' }, getCurrentDomain());
  };

  useEffect(() => {
    if (appDetails) {
      sendMessage({ message: 'sm:loaded' }, getCurrentDomain());
    }
  }, [appDetails]);

  useEffect(() => {
    updateMediaList(buildAppDetailsMediaFilesList(appDetails));
  }, [appDetails, updateMediaList]);

  return (
    <Box display="grid" gridTemplateRows="auto 1fr auto" gap={4} className={appDetailsModal}>
      <Box p={4}>
        <Typography variant="pageTitle" weight="medium">
          {appDetails.name}
        </Typography>
      </Box>
      <Box className={appDetailsModalBody}>
        <Box className={appDetailsHeader} py={7}>
          <Grid noBleeds>
            <Grid.Item lg={6} md={12} sm={12} xs={12}>
              <AppDetailsJumboHeader appDetails={appDetails} refreshDetails={refreshDetails} showCTA={false} />
            </Grid.Item>
            <Grid.Item lg={6} md={12} sm={12} xs={12}>
              <If condition={mediaList && mediaList[0]}>
                <DisplayMedia media={mediaList[0]} maxHeight="325" onClick={() => selectImageToShow(0)} />
              </If>
            </Grid.Item>
          </Grid>
        </Box>

        <If condition={mediaList && mediaList.length > 1}>
          <Box className={contentContainer} pt={6} pb={5}>
            <Grid noBleeds>
              {mediaList.map(
                (media, index) =>
                  index !== 0 &&
                  index <= 5 && (
                    <Grid.Item sm={3} xs={6} key={media.url}>
                      <DisplayMedia media={media} onClick={() => selectImageToShow(index)} />
                    </Grid.Item>
                  )
              )}
            </Grid>
          </Box>
        </If>
        <Box className={contentContainer} mb={8}>
          <AppDetailsFeatures featureList={appDetails.details.featureList} />
        </Box>
        <Box className={contentContainer} mb={5}>
          <AppDetailsInfo appDetails={appDetails} />
        </Box>
        <AppDetailsFooter appDetails={appDetails.details} />
        <If condition={mediaList.length > 0}>
          <Box className={showMedia ? appDetailsModalMediaOpened : appDetailsModalMediaClosed}>
            <Box display="flex" flexJustify="space-between" className={appDetailsModalMediaWrapper}>
              <Button
                aria-label="Previous Screenshot"
                color="secondary"
                onClick={() => selectPreviousMedia()}
                variant="text"
              >
                <IconArrowLeft size="xl" />
              </Button>
              <Box style={{ width: '75vw' }}>
                <DisplayMedia media={selectedMedia} maxHeight="80vh" />
              </Box>
              <Button aria-label="Next Screenshot" color="secondary" onClick={() => selectNextMedia()} variant="text">
                <IconArrowRight size="xl" />
              </Button>
            </Box>
          </Box>
        </If>
      </Box>
      <Box display="flex" flexJustify="space-between" p={4}>
        <Box display="inline-block">
          <If condition={showMedia}>
            <Button color="muted" onClick={() => setShowMedia(false)}>
              Back
            </Button>
          </If>
        </Box>
        <Box display="flex" flexJustify="flex-end">
          <Box display="inline-block" mr={4}>
            <Button color="alt" onClick={closeModal}>
              Cancel
            </Button>
          </Box>
          <Box display="inline-block">
            <AppDetailsActionButton inModal appDetails={appDetails} stretched={false} target="_blank" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

AppDetailsModal.propTypes = {
  appDetails: PropTypes.shape({
    id: PropTypes.string,
    isIntegration: PropTypes.bool,
    name: PropTypes.string,
    tagline: PropTypes.string,
    fullDescription: PropTypes.string,
    logo: PropTypes.string,
    installed: PropTypes.bool,
    details: PropTypes.shape({
      publisher: PropTypes.string,
      featureList: PropTypes.arrayOf(PropTypes.string),
      googleAnalyticsTrackingId: PropTypes.string,
      supportEmail: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
      supportUrl: PropTypes.string,
      requirements: PropTypes.arrayOf(PropTypes.string),
      screenshots: PropTypes.arrayOf(PropTypes.string),
      privacyPolicyUrl: PropTypes.string,
      termsOfUseUrl: PropTypes.string,
      blogUrl: PropTypes.string,
      youtubeUrl: PropTypes.string,
      websiteUrl: PropTypes.string,
      installUrl: PropTypes.string,
      upgradeRequired: PropTypes.bool,
      optionalUpgrade: PropTypes.bool,
      scopes: PropTypes.arrayOf(PropTypes.string),
      link: PropTypes.string,
      linkType: PropTypes.string,
    }),
  }).isRequired,
  refreshDetails: PropTypes.func.isRequired,
};

export default AppDetailsModal;
